// ============================================
// COMPONENT: AdminView - CON TABS
// ============================================
import React, { useState } from 'react';
import {
  Activity,
  Plus,
  Edit,
  Trash2,
  ArrowLeft,
  LogOut,
  ChevronDown,
  ChevronRight,
  Building2,
  X,
  UserCog,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;
const authHeaders = () => ({
  'Content-Type': 'application/json',
});

// ============================================
// MODAL INLINE DE EMPRESA
// ============================================
const CompanyModal = ({ company, plants = [], onClose, onSaved }) => {
  const isEdit = !!company;

  const [form, setForm] = useState({
    name:    company?.name    || '',
    rut:     company?.rut     || '',
    address: company?.address || '',
  });
  const [createAdmin, setCreateAdmin] = useState(false);
  const [adminForm, setAdminForm] = useState({ name: '', email: '', password: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  const set  = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const setA = (k, v) => setAdminForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('El nombre es requerido'); return; }
    setError(null);
    setSaving(true);

    try {
      let savedCompany;

      if (isEdit) {
        const res = await fetch(`${API_URL}/api/companies/${company.id}`, {
          method: 'PUT',
          headers: authHeaders(),
          credentials: 'include',
          body: JSON.stringify(form),
        });
        if (!res.ok) throw new Error((await res.json()).message || 'Error al actualizar');
        const data = await res.json(); savedCompany = data.company || data;
      } else {
        const res = await fetch(`${API_URL}/api/companies`, {
          method: 'POST',
          headers: authHeaders(),
          credentials: 'include',
          body: JSON.stringify(form),
        });
        if (!res.ok) throw new Error((await res.json()).message || 'Error al crear empresa');
        const data = await res.json(); savedCompany = data.company || data;
      }

      // Crear usuario administrador si corresponde
      if (!isEdit && createAdmin) {
        const { name, email, password } = adminForm;
        if (!name || !email || !password) {
          setError('Completa todos los campos del usuario administrador');
          setSaving(false);
          return;
        }
        const uRes = await fetch(`${API_URL}/api/users`, {
          method: 'POST',
          headers: authHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            name,
            email,
            password,
            role: 'Administrador',
            company_id: savedCompany.id,
            scope: 'company',
          }),
        });
        if (!uRes.ok) {
          const errBody = await uRes.json().catch(() => ({}));
          throw new Error(errBody.message || 'Empresa creada pero error al crear usuario');
        }
      }

      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-xl w-full max-w-lg border border-slate-700 shadow-2xl">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-slate-700">
          <h3 className="text-white font-bold text-lg">
            {isEdit ? 'Editar empresa' : 'Nueva empresa'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <p className="bg-red-900/50 border border-red-500 text-red-300 text-sm rounded px-3 py-2">
              {error}
            </p>
          )}

          <div>
            <label className="block text-slate-300 text-sm mb-1">Nombre <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={form.name}
              onChange={e => set('name', e.target.value)}
              className="w-full bg-slate-700 border border-slate-600 text-white rounded px-3 py-2 text-sm focus:outline-none focus:border-cyan-500"
              placeholder="Ej: Systech Chile SpA"
            />
          </div>

          <div>
            <label className="block text-slate-300 text-sm mb-1">RUT</label>
            <input
              type="text"
              value={form.rut}
              onChange={e => set('rut', e.target.value)}
              className="w-full bg-slate-700 border border-slate-600 text-white rounded px-3 py-2 text-sm focus:outline-none focus:border-cyan-500"
              placeholder="Ej: 76.123.456-7"
            />
          </div>

          <div>
            <label className="block text-slate-300 text-sm mb-1">Dirección</label>
            <input
              type="text"
              value={form.address}
              onChange={e => set('address', e.target.value)}
              className="w-full bg-slate-700 border border-slate-600 text-white rounded px-3 py-2 text-sm focus:outline-none focus:border-cyan-500"
              placeholder="Ej: Av. Providencia 1234, Santiago"
            />
          </div>

          {/* Crear admin — solo en modo creación */}
          {!isEdit && (
            <div className="border border-slate-600 rounded-lg p-4 space-y-3">
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={createAdmin}
                  onChange={e => setCreateAdmin(e.target.checked)}
                  className="w-4 h-4 accent-cyan-500"
                />
                <span className="text-slate-300 text-sm">Crear usuario administrador para esta empresa</span>
              </label>

              {createAdmin && (
                <div className="space-y-3 pt-1">
                  <div>
                    <label className="block text-slate-400 text-xs mb-1">Nombre</label>
                    <input
                      type="text"
                      value={adminForm.name}
                      onChange={e => setA('name', e.target.value)}
                      className="w-full bg-slate-700 border border-slate-600 text-white rounded px-3 py-2 text-sm focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-xs mb-1">Email</label>
                    <input
                      type="email"
                      value={adminForm.email}
                      onChange={e => setA('email', e.target.value)}
                      className="w-full bg-slate-700 border border-slate-600 text-white rounded px-3 py-2 text-sm focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-xs mb-1">Contraseña</label>
                    <input
                      type="password"
                      value={adminForm.password}
                      onChange={e => setA('password', e.target.value)}
                      className="w-full bg-slate-700 border border-slate-600 text-white rounded px-3 py-2 text-sm focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-700 text-slate-300 rounded hover:bg-slate-600 text-sm"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-cyan-600 text-white rounded hover:bg-cyan-700 text-sm disabled:opacity-50"
            >
              {saving ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear empresa'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const AdminView = ({
  user,
  users,
  plants,
  equipments,
  companies = [],
  selectedCompany = null,
  onBack,
  onLogout,
  onOpenMyData,
  onCreateUser,
  onEditUser,
  onDeleteUser,
  onCreatePlant,
  onEditPlant,
  onDeletePlant,
  onCreateEquipment,
  onEditEquipment,
  onDeleteEquipment,
  onCompaniesChange,  // callback para recargar empresas en App.js
}) => {
  const [activeTab,    setActiveTab]    = useState('Usuarios');
  const [expandedLines, setExpandedLines] = useState({});
  const [companyModal, setCompanyModal] = useState({ show: false, data: null });

  const isSuperAdmin = user?.role === 'Administrador' && user?.scope === 'global';
  const TABS = isSuperAdmin
    ? ['Usuarios', 'Plantas', 'Equipos', 'Empresas']
    : ['Usuarios', 'Plantas', 'Equipos'];

  // Filtrar por empresa seleccionada; null = Vista Global (sin filtro)
  const visiblePlants = selectedCompany == null
    ? plants
    : plants.filter(p => p.company_id === selectedCompany);
  const visibleEquipments = selectedCompany == null
    ? equipments
    : equipments.filter(e => {
        const plant = plants.find(p => p.id === e.plantId);
        return plant?.company_id === selectedCompany;
      });
  const visibleUsers = selectedCompany == null
    ? users
    : users.filter(u => u.company_id === selectedCompany);

  const toggleLine = (plantId, line) => {
    const key = `${plantId}-${line}`;
    setExpandedLines(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleDeleteCompany = async (id) => {
    if (!window.confirm('¿Eliminar esta empresa? Esta acción no se puede deshacer.')) return;
    try {
      const res = await fetch(`${API_URL}/api/companies/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
        credentials: 'include',
      });
      if (!res.ok) throw new Error((await res.json()).message || 'Error al eliminar');
      onCompaniesChange?.();
    } catch (err) {
      alert(err.message);
    }
  };

  const plantCountFor = (companyId) => plants.filter(p => p.company_id === companyId).length;

  // ----------------------------------------
  // Tab: Usuarios
  // ----------------------------------------
  const renderUsuarios = () => (
    <div className="bg-slate-800 rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-bold text-white">Usuarios</h3>
        <button
          onClick={onCreateUser}
          className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-2"
        >
          <Plus size={16} /> Nuevo
        </button>
      </div>
      <div className="space-y-2">
        {visibleUsers.map(u => (
          <div key={u.id} className="bg-slate-700 p-3 rounded flex justify-between items-center">
            <div>
              <p className="text-white font-bold text-sm">{u.name}</p>
              <p className="text-slate-400 text-xs">{u.email}</p>
              <p className="text-slate-400 text-xs">{u.role}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => onEditUser(u)} className="text-blue-400 hover:text-blue-300">
                <Edit size={16} />
              </button>
              <button onClick={() => onDeleteUser(u.id)} className="text-red-400 hover:text-red-300">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
        {visibleUsers.length === 0 && (
          <p className="text-slate-400 text-center py-4">No hay usuarios</p>
        )}
      </div>
    </div>
  );

  // ----------------------------------------
  // Tab: Plantas
  // ----------------------------------------
  const renderPlantas = () => (
    <div className="bg-slate-800 rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-bold text-white">Plantas</h3>
        <button
          onClick={onCreatePlant}
          className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-2"
        >
          <Plus size={16} /> Nueva
        </button>
      </div>
      <div className="space-y-2">
        {visiblePlants.map(p => (
          <div key={p.id} className="bg-slate-700 p-3 rounded flex justify-between items-center">
            <div>
              <p className="text-white font-bold text-sm">{p.name}</p>
              <p className="text-slate-400 text-xs">{p.code} - {p.location}</p>
              <p className="text-slate-400 text-xs">{p.lines} líneas</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => onEditPlant(p)} className="text-blue-400 hover:text-blue-300">
                <Edit size={16} />
              </button>
              <button onClick={() => onDeletePlant(p.id)} className="text-red-400 hover:text-red-300">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
        {visiblePlants.length === 0 && (
          <p className="text-slate-400 text-center py-4">No hay plantas</p>
        )}
      </div>
    </div>
  );

  // ----------------------------------------
  // Tab: Equipos
  // ----------------------------------------
  const renderEquipos = () => (
    <div className="bg-slate-800 rounded-lg p-6">
      <h3 className="text-2xl font-bold text-white mb-4">Equipos por Planta y Línea</h3>
      {visiblePlants.map(plant => {
        const plantEquipments = visibleEquipments.filter(e => e.plantId === plant.id);
        return (
          <div key={plant.id} className="mb-6 bg-slate-700 rounded-lg p-4">
            <h4 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
              <Activity size={20} className="text-cyan-400" />
              {plant.name} ({plant.code})
            </h4>
            {Array.from({ length: plant.lines }).map((_, idx) => {
              const lineNum  = `L${idx + 1}`;
              const lineEquip  = plantEquipments.filter(e => e.line === lineNum);
              const isExpanded = expandedLines[`${plant.id}-${lineNum}`];
              return (
                <div key={lineNum} className="mb-3 bg-slate-600 rounded-lg">
                  <div
                    className="flex justify-between items-center p-3 cursor-pointer hover:bg-slate-550"
                    onClick={() => toggleLine(plant.id, lineNum)}
                  >
                    <div className="flex items-center gap-3">
                      {isExpanded
                        ? <ChevronDown  size={20} className="text-cyan-400" />
                        : <ChevronRight size={20} className="text-cyan-400" />}
                      <h5 className="text-lg font-bold text-white">Línea {lineNum}</h5>
                      <span className="text-sm text-slate-400">({lineEquip.length} equipos)</span>
                    </div>
                    <button
                      onClick={ev => { ev.stopPropagation(); onCreateEquipment(plant.id, lineNum); }}
                      className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 flex items-center gap-1"
                    >
                      <Plus size={14} /> Agregar
                    </button>
                  </div>
                  {isExpanded && (
                    <div className="p-3 border-t border-slate-500">
                      {lineEquip.length === 0 ? (
                        <p className="text-slate-400 text-sm italic">No hay equipos en esta línea</p>
                      ) : (
                        <div className="space-y-2">
                          {lineEquip.map(e => (
                            <div key={e.id} className="bg-slate-500 p-3 rounded flex justify-between items-center">
                              <div className="flex-1">
                                <p className="text-white font-bold text-sm">{e.name}</p>
                                {e.code && <p className="text-slate-300 text-xs">Código: {e.code}</p>}
                                <p className="text-slate-300 text-xs">
                                  Marca: {e.manufacturer || 'N/A'} · Modelo: {e.model} · Serie: {e.serial}
                                </p>
                                <p className="text-slate-400 text-xs mt-1">
                                  Costo: ${e.cost?.toLocaleString('es-CL')}
                                </p>
                                <span className={`inline-block mt-2 px-2 py-1 rounded text-xs font-semibold ${
                                  e.status === 'operativo'     ? 'bg-green-600 text-white' :
                                  e.status === 'falla'         ? 'bg-red-600 text-white'   :
                                                                  'bg-yellow-600 text-white'
                                }`}>
                                  {e.status?.toUpperCase() || 'DESCONOCIDO'}
                                </span>
                              </div>
                              <div className="flex gap-2 ml-4">
                                <button onClick={() => onEditEquipment(e, plant.id, lineNum)} className="text-blue-400 hover:text-blue-300">
                                  <Edit size={16} />
                                </button>
                                <button onClick={() => onDeleteEquipment(e.id)} className="text-red-400 hover:text-red-300">
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
      {visiblePlants.length === 0 && (
        <p className="text-slate-400 text-center py-8">No hay plantas registradas. Crea una planta primero.</p>
      )}
    </div>
  );

  // ----------------------------------------
  // Tab: Empresas
  // ----------------------------------------
  const renderEmpresas = () => (
    <div className="bg-slate-800 rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-bold text-white">Empresas</h3>
        <button
          onClick={() => setCompanyModal({ show: true, data: null })}
          className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-2"
        >
          <Plus size={16} /> Nueva Empresa
        </button>
      </div>

      <div className="space-y-3">
        {companies.map(c => (
          <div key={c.id} className="bg-slate-700 p-4 rounded-lg flex justify-between items-start">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-slate-600 rounded-lg mt-0.5">
                <Building2 size={20} className="text-cyan-400" />
              </div>
              <div>
                <p className="text-white font-bold">{c.name}</p>
                {c.rut     && <p className="text-slate-400 text-xs mt-0.5">RUT: {c.rut}</p>}
                {c.address && <p className="text-slate-400 text-xs">Dirección: {c.address}</p>}
                <span className="inline-block mt-2 px-2 py-0.5 bg-cyan-900 text-cyan-300 text-xs rounded-full font-semibold">
                  {plantCountFor(c.id)} planta{plantCountFor(c.id) !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                onClick={() => setCompanyModal({ show: true, data: c })}
                className="text-blue-400 hover:text-blue-300"
              >
                <Edit size={16} />
              </button>
              <button
                onClick={() => handleDeleteCompany(c.id)}
                className="text-red-400 hover:text-red-300"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
        {companies.length === 0 && (
          <p className="text-slate-400 text-center py-8">No hay empresas registradas</p>
        )}
      </div>
    </div>
  );

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="min-h-screen bg-slate-900">
      {/* Navigation */}
      <nav className="bg-slate-800 px-6 py-4 border-b border-slate-700">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-6">
            <button onClick={onBack} className="text-cyan-400 hover:text-cyan-300">
              <ArrowLeft size={24} />
            </button>
            <Activity size={32} className="text-cyan-400" />
            <div className="text-white">
              <h1 className="text-xl font-bold">Panel de Administración</h1>
              <p className="text-xs text-slate-400">{user.email} - {user.role}</p>
            </div>
            {selectedCompany != null && (
              <span className="px-3 py-1 bg-slate-700 text-cyan-400 text-xs font-semibold rounded-lg">
                {(companies.find(c => c.id === Number(selectedCompany)) || (user.companies || []).find(c => c.id === Number(selectedCompany)))?.name || `Empresa ${selectedCompany}`}
              </span>
            )}
            {selectedCompany == null && isSuperAdmin && (
              <span className="px-3 py-1 bg-slate-700 text-slate-400 text-xs rounded-lg">Vista Global</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {onOpenMyData && (
              <button onClick={onOpenMyData} title="Mis Datos" className="p-2 text-slate-300 hover:text-white flex items-center gap-1 text-sm">
                <UserCog size={15} /><span className="hidden sm:inline">Mis Datos</span>
              </button>
            )}
            <button
              onClick={onLogout}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-2"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="p-6">
        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-slate-700">
          {TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-3 text-sm font-semibold rounded-t transition-colors ${
                activeTab === tab
                  ? 'bg-slate-800 text-cyan-400 border-b-2 border-cyan-400'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'Usuarios'  && renderUsuarios()}
        {activeTab === 'Plantas'   && renderPlantas()}
        {activeTab === 'Equipos'   && renderEquipos()}
        {activeTab === 'Empresas'  && isSuperAdmin && renderEmpresas()}
      </div>

      {/* Modal de empresa */}
      {companyModal.show && (
        <CompanyModal
          company={companyModal.data}
          plants={plants}
          onClose={() => setCompanyModal({ show: false, data: null })}
          onSaved={() => {
            setCompanyModal({ show: false, data: null });
            onCompaniesChange?.();
          }}
        />
      )}
    </div>
  );
};

export default AdminView;
